// Enhanced messaging system hooks (REQ-0002, REQ-0003, REQ-0004)
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { createLogger } from '@xanectech/lib';
import { supabase } from '@xanectech/supabase-client';
import type {
  MessageWithReadStatus,
  MessageFilter,
  MessageRead,
} from '@xanectech/types';
import { MessagingService } from '../services/MessagingService';
import { useToast } from '@xanectech/ui/lib';
// import { useToast } from '@xanectech/ui/lib';

const logger = createLogger('Data - useMessages');

const isMessageType = (
  value: string,
): value is MessageWithReadStatus['type'] => {
  return (
    value === 'info' ||
    value === 'warning' ||
    value === 'success' ||
    value === 'error'
  );
};

const isTargetType = (
  value: string,
): value is MessageWithReadStatus['target_type'] => {
  return value === 'system' || value === 'user' || value === 'company';
};

// Re-export MessageRead for backward compatibility
export type { MessageRead } from '@xanectech/types';

// Hook to get all messages for a user (system + company + user-specific)
export const useMessages = (
  filter: MessageFilter,
): UseQueryResult<MessageWithReadStatus[]> => {
  return useQuery({
    queryKey: ['messages', filter.userId, filter.companyId],
    queryFn: async () => {
      try {
        const result = await MessagingService.getAllMessages(filter);
        logger.info('useMessages queryFn result:', {
          messageCount: result.length,
        });
        return result;
      } catch (error) {
        logger.error('useMessages queryFn error:', {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        });
        throw error;
      }
    },
    enabled: !!filter.userId,
    refetchInterval: 30000, // Refetch every 30 seconds for consistency with useUnreadMessageCount
  });
};

/**
 * @deprecated Use useMessages instead. This hook will be removed in Phase 2.
 * Migration: Replace `useUserMessages(userId)` with `useMessages({ userId, userRole, companyId })`
 */
export const useUserMessages = (userId?: string) => {
  return useQuery({
    queryKey: ['user-messages', userId],
    queryFn: async () => {
      if (!userId) throw new Error('User ID is required');

      // Get messages with read status
      const { data: messages, error: messagesError } = await supabase
        .from('messages')
        .select('*')
        .order('created_at', { ascending: false });

      if (messagesError) throw messagesError;

      // Get read status for current user
      const { data: reads, error: readsError } = await supabase
        .from('message_reads')
        .select('*')
        .eq('user_id', userId);

      if (readsError) throw readsError;

      // Combine messages with read status
      const messagesWithReadStatus: MessageWithReadStatus[] = messages.map(
        (message) => {
          const readRecord = reads.find(
            (read) => read.message_id === message.id,
          );

          const normalizedType: MessageWithReadStatus['type'] = isMessageType(
            message.type,
          )
            ? message.type
            : 'info';

          const normalizedTargetType: MessageWithReadStatus['target_type'] =
            isTargetType(message.target_type) ? message.target_type : 'system';

          return {
            ...message,
            type: normalizedType,
            target_type: normalizedTargetType,
            user_id: message.user_id ?? undefined,
            company_id: message.company_id ?? undefined,
            read: Boolean(readRecord),
            read_at: readRecord?.read_at ?? undefined,
          };
        },
      );

      return messagesWithReadStatus;
    },
    enabled: !!userId,
  });
};

export const useMarkMessageAsRead = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      messageId,
      target_type,
      userId,
    }: {
      messageId: string;
      target_type: 'system' | 'user' | 'company';
      userId: string;
    }) => {
      const { data, error } = await supabase
        .from('message_reads')
        .upsert({
          message_id: messageId,
          user_id: userId,
          target_type: target_type,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate message queries for this specific user
      void queryClient.invalidateQueries({
        queryKey: ['messages', variables.userId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['user-messages', variables.userId],
      });
      // Invalidate unread count queries for this specific user
      // We use a predicate to match all query keys starting with 'unread-message-count'
      // followed by this userId, regardless of companyId
      void queryClient.invalidateQueries({
        predicate: (query) => {
          const [queryType, queryUserId] = query.queryKey;
          return (
            queryType === 'unread-message-count' &&
            queryUserId === variables.userId
          );
        },
      });
      void queryClient.invalidateQueries({
        queryKey: ['unread-company-message-count', variables.userId],
      });
    },
    onError: (error) => {
      logger.error('Failed to mark message as read', { error });
      toast({
        title: 'Error',
        description: `Failed to mark message as read: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
};

export const useCreateMessage = () => {
  const queryClient = useQueryClient();
  // const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      userId,
      companyId,
      targetType,
      title,
      content,
      type = 'info',
    }: {
      userId?: string | null;
      companyId?: string | null;
      targetType: 'system' | 'user' | 'company';
      title: string;
      content: string;
      type?: 'info' | 'warning' | 'success' | 'error';
    }) => {
      const { data, error } = await supabase
        .from('messages')
        .insert({
          user_id: userId,
          company_id: companyId,
          target_type: targetType,
          title,
          content,
          type,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['user-messages'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['admin-messages'],
      });
      // toast({
      //   title: 'Message Created',
      //   description: 'Message has been successfully created.',
      // });
    },
    onError: (error) => {
      logger.error('Failed to create message', { error });
      // toast({
      //   title: 'Error',
      //   description: `Failed to create message: ${error.message}`,
      //   variant: 'destructive',
      // });
    },
  });
};

// Hook for admin message management (REQ-0004)
export const useAdminMessages = () => {
  return useQuery({
    queryKey: ['admin-messages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });
};

export const useUpdateMessage = () => {
  const queryClient = useQueryClient();
  // const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      messageId,
      title,
      content,
      type,
    }: {
      messageId: string;
      title: string;
      content: string;
      type: 'info' | 'warning' | 'success' | 'error';
    }) => {
      const { data, error } = await supabase
        .from('messages')
        .update({
          title,
          content,
          type,
        })
        .eq('id', messageId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['admin-messages'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['user-messages'],
      });
      // toast({
      //   title: 'Message Updated',
      //   description: 'Message has been successfully updated.',
      // });
    },
    onError: (error) => {
      logger.error('Failed to update message', { error });
      // toast({
      //   title: 'Error',
      //   description: `Failed to update message: ${error.message}`,
      //   variant: 'destructive',
      // });
    },
  });
};

export const useDeleteMessage = () => {
  const queryClient = useQueryClient();
  // const { toast } = useToast();

  return useMutation({
    mutationFn: async (messageId: string) => {
      const { error } = await supabase
        .from('messages')
        .delete()
        .eq('id', messageId);

      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['admin-messages'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['user-messages'],
      });
      // toast({
      //   title: 'Message Deleted',
      //   description: 'Message has been successfully deleted.',
      // });
    },
    onError: (error) => {
      logger.error('Failed to delete message', { error });
      // toast({
      //   title: 'Error',
      //   description: `Failed to delete message: ${error.message}`,
      //   variant: 'destructive',
      // });
    },
  });
};

// Hook to get unread message count for user messages
export const useUnreadMessageCount = (userId?: string, companyId?: string) => {
  return useQuery({
    queryKey: ['unread-message-count', userId, companyId],
    queryFn: async () => {
      if (!userId) return 0;
      return await MessagingService.getUnreadCount(userId, companyId);
    },
    enabled: !!userId,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

// Hook to get unread company message count
/**
 * @deprecated Use useUnreadMessageCount instead. This hook will be removed in Phase 2.
 * Migration: Replace `useUnreadCompanyMessageCount(userId)` with `useUnreadMessageCount(userId, companyId)`
 * Note: This hook doesn't properly filter by company and may return incorrect counts.
 */
export const useUnreadCompanyMessageCount = (userId?: string) => {
  return useQuery({
    queryKey: ['unread-company-message-count', userId],
    queryFn: async () => {
      if (!userId) return 0;

      // Get company messages (company and system types)
      const { data: messages, error: messagesError } = await supabase
        .from('messages')
        .select('id')
        .in('target_type', ['company', 'system'])
        .order('created_at', { ascending: false });

      if (messagesError) throw messagesError;

      // Get read status for current user
      const { data: reads, error: readsError } = await supabase
        .from('message_reads')
        .select('message_id')
        .eq('user_id', userId);

      if (readsError) throw readsError;

      const readMessageIds = new Set<string>(
        (reads ?? []).map((read) => read.message_id) as string[],
      );
      const unreadCount = messages.filter(
        (message) => !readMessageIds.has(message.id as string),
      ).length;

      return unreadCount;
    },
    enabled: !!userId,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};
